export type RuntimeBase = '/learn/path';
export type RuntimeSession = {
  uid: string;
  token?: string;
};

type RuntimeQuery = Record<string, string | number | boolean | null | undefined>;

export function resolveRuntimeBase(_pathname?: string): RuntimeBase {
  return '/learn/path';
}

export function buildRuntimePath(base: RuntimeBase, slug: string): string {
  return `${base}/${slug}`;
}

function buildRuntimeQueryString(session: RuntimeSession, query?: RuntimeQuery): string {
  const params = new URLSearchParams();
  if (session.uid) params.set('uid', session.uid);
  if (session.token) params.set('token', session.token);
  Object.entries(query ?? {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    params.set(k, String(v));
  });
  return params.toString();
}

function buildRuntimeScopedPath(basePath: string, session: RuntimeSession, query?: RuntimeQuery): string {
  const qs = buildRuntimeQueryString(session, query);
  return `${basePath}${qs ? `?${qs}` : ''}`;
}

export function buildRuntimeHomePath(base: RuntimeBase, slug: string, session: RuntimeSession): string {
  return buildRuntimeScopedPath(`${base}/${slug}/home`, session);
}

export function buildRuntimeLessonPath(base: RuntimeBase, slug: string, lessonId: string, session: RuntimeSession): string {
  return buildRuntimeScopedPath(`${base}/${slug}/lesson/${lessonId}`, session);
}

export function buildRuntimeSummaryPath(base: RuntimeBase, slug: string, lessonId: string, session: RuntimeSession, query: RuntimeQuery): string {
  return buildRuntimeScopedPath(`${base}/${slug}/summary/${lessonId}`, session, query);
}

export function buildRuntimeProfilePath(base: RuntimeBase, session: RuntimeSession): string {
  const root = `${base}/profile`;
  return buildRuntimeScopedPath(root, session);
}


export function buildRuntimePracticePath(base: RuntimeBase, slug: string, session: RuntimeSession): string {
  return buildRuntimeScopedPath(`${base}/${slug}/practice`, session);
}

export function buildRuntimePracticeSessionPath(base: RuntimeBase, slug: string, session: RuntimeSession, query?: RuntimeQuery): string {
  return buildRuntimeScopedPath(`${base}/${slug}/practice/session`, session, query);
}

export function buildRuntimeExamPath(base: RuntimeBase, slug: string, session: RuntimeSession, query?: RuntimeQuery): string {
  return buildRuntimeScopedPath(`${base}/${slug}/exam`, session, query);
}

export function buildRuntimeExamSessionPath(base: RuntimeBase, slug: string, session: RuntimeSession, query?: RuntimeQuery): string {
  return buildRuntimeScopedPath(`${base}/${slug}/exam/session`, session, query);
}

export function buildRuntimeExamResultPath(base: RuntimeBase, slug: string, session: RuntimeSession, query?: RuntimeQuery): string {
  return buildRuntimeScopedPath(`${base}/${slug}/exam/result`, session, query);
}

export function buildRuntimeReviewPath(base: RuntimeBase, slug: string, session: RuntimeSession, query?: RuntimeQuery): string {
  return buildRuntimeScopedPath(`${base}/${slug}/review`, session, query);
}

export function buildRuntimeReviewDetailPath(base: RuntimeBase, slug: string, attemptId: string, session: RuntimeSession, query?: RuntimeQuery): string {
  return buildRuntimeScopedPath(`${base}/${slug}/review/${attemptId}`, session, query);
}

export function buildRuntimeReviewRetryPath(base: RuntimeBase, slug: string, attemptId: string, session: RuntimeSession, query?: RuntimeQuery): string {
  return buildRuntimeScopedPath(`${base}/${slug}/review/retry/${attemptId}`, session, query);
}

export function buildRuntimeTutorPath(base: RuntimeBase, slug: string, session: RuntimeSession, query?: RuntimeQuery): string {
  return buildRuntimeScopedPath(`${base}/${slug}/tutor`, session, query);
}

export function buildRuntimeFlashcardsPath(base: RuntimeBase, slug: string, session: RuntimeSession, query?: RuntimeQuery): string {
  return buildRuntimeScopedPath(`${base}/${slug}/flashcards`, session, query);
}

export function buildRuntimeStudyPlanPath(base: RuntimeBase, slug: string, session: RuntimeSession, query?: RuntimeQuery): string {
  return buildRuntimeScopedPath(`${base}/${slug}/study-plan`, session, query);
}

export function buildRuntimeCommunityPath(base: RuntimeBase, slug: string, session: RuntimeSession, query?: RuntimeQuery): string {
  return buildRuntimeScopedPath(`${base}/${slug}/community`, session, query);
}

export function buildRuntimeReversePath(base: RuntimeBase, slug: string, session: RuntimeSession, query?: RuntimeQuery): string {
  return buildRuntimeScopedPath(`${base}/${slug}/reverse`, session, query);
}

export function buildRuntimeReverseSessionPath(base: RuntimeBase, slug: string, session: RuntimeSession, query?: RuntimeQuery): string {
  return buildRuntimeScopedPath(`${base}/${slug}/reverse/session`, session, query);
}

export function buildRuntimeReverseResultPath(base: RuntimeBase, slug: string, session: RuntimeSession, query?: RuntimeQuery): string {
  return buildRuntimeScopedPath(`${base}/${slug}/reverse/result`, session, query);
}
